import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Logo } from '@/components/Logo';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="forgot-page">
      <div className="hidden lg:flex bg-ink text-white p-12 relative kc-noise">
        <div className="absolute inset-0 gold-sheen pointer-events-none"/>
        <div className="relative flex flex-col justify-between w-full">
          <Logo invert/>
          <div>
            <h1 className="font-display uppercase text-5xl xl:text-6xl leading-none">Locked out?<br/><span className="text-[color:var(--kc-gold)]">We'll get you back in.</span></h1>
            <p className="mt-4 text-white/80 max-w-md">Pop your email below. If you have an account, we'll send a reset link to your inbox — good for one hour.</p>
          </div>
          <div/>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-kc hover:text-ink mb-6" data-testid="forgot-back"><ArrowLeft size={12}/> Back to login</Link>
          <div className="lg:hidden mb-6"><Logo/></div>

          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-gold-soft border-2 border-[color:var(--kc-gold)] flex items-center justify-center mb-4">
                <CheckCircle2 size={28} className="text-[color:var(--kc-gold-2)]"/>
              </div>
              <h2 className="font-display uppercase text-3xl md:text-4xl">Check your email.</h2>
              <p className="mt-3 text-muted-kc">If an account exists for <strong className="text-ink">{email}</strong>, a reset link is on its way. It expires in 1 hour.</p>
              <p className="mt-4 text-xs text-muted-kc">Not seeing it? Check spam, or try again in a couple minutes.</p>
              <Link to="/login" className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold w-full">Back to login</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <h2 className="font-display uppercase text-3xl md:text-4xl">Forgot password</h2>
              <p className="mt-1 text-muted-kc text-sm">Enter your email and we'll send a reset link.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-kc"/>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="forgot-email-input" autoComplete="email"/>
                  </div>
                </div>
                {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="forgot-error">{err}</div>}
                <button type="submit" disabled={loading} className="w-full h-11 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 disabled:opacity-60" data-testid="forgot-submit">{loading ? 'Sending…' : 'Send reset link'}</button>
              </div>
              <div className="mt-6 text-sm text-muted-kc">Remembered it? <Link to="/login" className="font-semibold text-ink hover:text-gold transition-colors">Log in</Link></div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
